import { css, StyleDeclarationValue } from 'aphrodite';
import * as React from 'react';
import { useRouteMatch } from 'react-router';
import { CustomPageType } from '../../../../../extViewmodels/sdk/models/CustomPageStyle';
import { useClientGetSurveyLogoByToken } from '../../../../../queries';
import { LogoIcon } from '../../../../../web/components/svgs/icons/LogoIcon';
import { black } from '../../../../../web/styles/colors';
import { ICustomSurvey, IDonationSurvey, IEventRegistrationSurvey, ISatisfactionSurvey } from '../../../../models/Api';
import { useCustomPageStyleByToken } from '../../../../queries/useCustomPageStyleByToken';
import { SurveyHeader } from '../components/SurveyHeader';
import { styleSheet } from './styles';

interface IProps<T extends ISatisfactionSurvey | ICustomSurvey | IEventRegistrationSurvey | IDonationSurvey> {
	cardStyles?: StyleDeclarationValue[];
	className?: string;
	contentStyles?: StyleDeclarationValue[];
	styles?: StyleDeclarationValue[];
	survey?: T;
	showCustomContent?: boolean;
	onSurveyLoaded?: () => void;
}

export function Survey<T extends ISatisfactionSurvey | ICustomSurvey | IEventRegistrationSurvey | IDonationSurvey>({
	cardStyles = [],
	children,
	className = '',
	contentStyles = [],
	styles = [],
	survey,
	onSurveyLoaded,
}: React.PropsWithChildren<IProps<T>>) {
	const { params: urlParams = {} } = useRouteMatch<{ token?: string }>();

	const { data: customHeaderData } = useCustomPageStyleByToken({
		token: urlParams.token,
		type: CustomPageType.Header,
		enabled: !!urlParams.token,
	});

	const { data: customFooterData } = useCustomPageStyleByToken({
		token: urlParams.token,
		type: CustomPageType.Footer,
		enabled: !!urlParams.token,
	});

	const surveyLogo = useClientGetSurveyLogoByToken({
		accessToken: urlParams.token,
		enabled: survey?._type === 'CustomSurvey',
		refetchOnWindowFocus: false,
	});

	const renderCustomContent = React.useCallback((html: string, isHeader = false) => {
		const classNameStyle = isHeader ? css(styleSheet.customHeader) : css(styleSheet.customFooter);
		return (
			<div
				className={classNameStyle}
				dangerouslySetInnerHTML={{ __html: html }}
				{...(isHeader ? { 'data-survey-header': true } : {})}
			/>
		);
	}, []);

	const onRenderCustomHeader = React.useCallback(() => {
		if (customHeaderData?.html) {
			return renderCustomContent(customHeaderData.html, true);
		}
		onSurveyLoaded?.();
		return null;
	}, [customHeaderData?.html, onSurveyLoaded, renderCustomContent]);

	const onRenderCustomFooter = React.useCallback(() => {
		if (customFooterData?.html) {
			return renderCustomContent(customFooterData.html, false);
		}
		return null;
	}, [customFooterData?.html, renderCustomContent]);

	return (
		<div className={`survey-container ${className} ${css(styleSheet.container, ...styles)}`}>
			<div className={css(styleSheet.content, ...cardStyles)}>
				{onRenderCustomHeader() || (
					<SurveyHeader
						companyDisplayName={
							survey?._type === 'CustomSurvey' && 'options' in survey
								? survey.options?.companyName
								: survey?.companyDisplayName
						}
						name={survey?.name || 'Survey'}
						logoUrl={surveyLogo.data}
						styles={
							survey?._type === 'EventRegistrationSurvey'
								? [styleSheet.headerEvent]
								: survey?._type === 'SatisfactionSurvey'
									? [styleSheet.headerSatisfaction]
									: []
						}
					/>
				)}

				<div className={css(styleSheet.body, ...contentStyles)}>{children}</div>
			</div>
			<div className={css(styleSheet.footer)}>
				{onRenderCustomFooter() || (
					<>
						<div className={css(styleSheet.poweredBy)}>Powered by</div>
						<a href='https://levitate.ai' target='_blank' rel='noopener noreferrer'>
							<LogoIcon className={css(styleSheet.mainLogo)} textColor={black} />
						</a>
					</>
				)}
			</div>
		</div>
	);
}
